/* eslint-disable sonarjs/max-switch-cases */
import { TranslateService } from '@ngx-translate/core';
import { DateTime } from 'luxon';
import { Observable, of } from 'rxjs';

import { RouteSegment } from '@core/enums/route-segment.enum';

import { TimelineTypeStoreModelEnum } from '@domain/charger-details/charger-details.enums';
import {
  AdaptivePowerCapChangeEventStoreModel,
  AuthorizationFailedEventStoreModel,
  AuthorizationSuccessfulEventStoreModel,
  BootNotificationEventStoreModel,
  CSFaultedEventStoreModel,
  CSStateChangeEventStoreModel,
  ChargingFaultedEventStoreModel,
  ChargingTimelineEventStoreModel,
  ChargingTimelineResponseStoreModel,
  CustomCommandEventStoreModel,
  EvseFaultedEventStoreModel,
  ForceDisconnectEventStoreModel,
  HardResetEventStoreModel,
  ManualPriorityClearedEventStoreModel,
  ManualPrioritySetEventStoreModel,
  PowerLimitChangeEventStoreModel,
  PowerLimitChangeRejectedEventStoreModel,
  PowerOverrideClearedEventStoreModel,
  SessionClosedEventStoreModel,
  SessionOpenedEventStoreModel,
  SoftResetEventStoreModel,
  StatusNotificationEventStoreModel,
  TransactionBlockedEventStoreModel,
  TransactionRemoteStartEventStoreModel,
  TransactionRemoteStopEventStoreModel,
  TransactionStartedEventStoreModel,
  TransactionStoppedEventStoreModel,
  TriggerMessageEventStoreModel,
  UnknownVehiclePluggedInEventStoreModel,
  UnlockConnectorEventStoreModel,
  UnrecognizableOCPPMessageEventStoreModel,
  VehiclePluggedInEventStoreModel,
  VehicleUnpluggedEventStoreModel,
} from '@domain/charger-details/charger-details.models';
import { OcppChargePointStatusStoreModelEnum } from '@domain/single-site/single-site.enums';

import { TimelineEvent, TimelineEventDetail } from '@shared/components/timeline-core';
import { PartType } from '@shared/modules/complex-translation/models/enums/part-type.enum';
import { PartConfigUnion } from '@shared/modules/complex-translation/models/types/part-config-union';
import { KwPipe } from '@shared/pipes/kw';

interface EventTranslation {
  title$: Observable<string>;
  description$: Observable<string>;
  titlePartConfigs?: PartConfigUnion[];
  descriptionPartConfigs?: PartConfigUnion[];
  details?: TimelineEventDetail[];
}

const hiddenStatusNotificationStatuses = new Set([
  OcppChargePointStatusStoreModelEnum.Available,
  OcppChargePointStatusStoreModelEnum.Charging,
  OcppChargePointStatusStoreModelEnum.Finishing,
]);

export const isChargingTimelineEventVisible = (event: ChargingTimelineEventStoreModel): boolean => {
  return (
    event.type !== TimelineTypeStoreModelEnum.StatusNotificationEvent ||
    !hiddenStatusNotificationStatuses.has(event.status as OcppChargePointStatusStoreModelEnum)
  );
};

export const getNextTimelinePageBeforeTimeWhenNoEventsAreVisible = (timeline: ChargingTimelineResponseStoreModel): string | undefined => {
  if (timeline.events.some((event) => isChargingTimelineEventVisible(event))) {
    return undefined;
  }

  const lastEventTimestampISO = timeline.events.length > 0 ? timeline.events.at(-1)?.timestamp : undefined;
  const lastEventTimestamp = lastEventTimestampISO ? DateTime.fromISO(lastEventTimestampISO) : undefined;
  const oldestTimestamp = timeline.oldest ? DateTime.fromISO(timeline.oldest) : undefined;

  if (!lastEventTimestamp || !oldestTimestamp || lastEventTimestamp <= oldestTimestamp) {
    return undefined;
  }

  return lastEventTimestampISO;
};

export const mapChargingTimelineEventStoreModelToTimelineEvent = (
  evses: {
    id: string;
    name: string;
  }[],
  event: ChargingTimelineEventStoreModel,
  translateService: TranslateService,
  kwPipe: KwPipe,
  siteId: string
): TimelineEvent => {
  const { title$, description$, titlePartConfigs, descriptionPartConfigs, details } = translateChargingTimelineEventStoreModel(
    evses,
    event,
    translateService,
    kwPipe,
    siteId
  );

  return {
    id: JSON.stringify(event),
    dateTime: DateTime.fromISO(event.timestamp),
    severity: event.severity,
    title$,
    titlePartConfigs,
    description$,
    descriptionPartConfigs,
    details,
  };
};

export const translateChargingTimelineEventStoreModel = (
  evses: {
    id: string;
    name: string;
  }[],
  event: ChargingTimelineEventStoreModel,
  translateService: TranslateService,
  kwPipe: KwPipe,
  siteId: string
): EventTranslation => {
  const evseName = evses.find((evse) => evse.id === event.assetId)?.name;

  switch (event.type) {
    case TimelineTypeStoreModelEnum.AdaptivePowerCapChangeEvent: {
      return translateAdaptivePowerCapChangeEventStoreModel(event, translateService, kwPipe, evseName);
    }
    case TimelineTypeStoreModelEnum.AuthorizationFailedEvent: {
      return translateAuthorizationFailedEventStoreModel(event, translateService, evseName);
    }
    case TimelineTypeStoreModelEnum.AuthorizationSuccessfulEvent: {
      return translateAuthorizationSuccessfulEventStoreModel(event, translateService, evseName);
    }
    case TimelineTypeStoreModelEnum.BootNotificationEvent: {
      return translateBootNotificationEventStoreModel(event, translateService);
    }
    case TimelineTypeStoreModelEnum.ChargingFaultedEvent: {
      return translateChargingFaultedEventStoreModel(event, translateService, siteId, evseName);
    }
    case TimelineTypeStoreModelEnum.CsFaultedEvent: {
      return translateCsFaultedEventStoreModel(event, translateService);
    }
    case TimelineTypeStoreModelEnum.EvseFaultedEvent: {
      return translateEvseFaultedEventStoreModel(event, translateService, evseName);
    }
    case TimelineTypeStoreModelEnum.StatusNotificationEvent: {
      return translateStatusNotificationEventStoreModel(event, translateService, evseName);
    }
    case TimelineTypeStoreModelEnum.CsStateChangeEvent: {
      return translateCsStateChangeEventStoreModel(event, translateService);
    }
    case TimelineTypeStoreModelEnum.CustomCommandEvent: {
      return translateCustomCommandEventStoreModel(event, translateService);
    }
    case TimelineTypeStoreModelEnum.ForceDisconnectEvent: {
      return translateForceDisconnectEventStoreModel(event, translateService);
    }
    case TimelineTypeStoreModelEnum.HardResetEvent: {
      return translateHardResetEventStoreModel(event, translateService);
    }
    case TimelineTypeStoreModelEnum.SoftResetEvent: {
      return translateSoftResetEventStoreModel(event, translateService);
    }
    case TimelineTypeStoreModelEnum.PowerLimitChangeEvent: {
      return translatePowerLimitChangeEventStoreModel(event, translateService, kwPipe, evseName);
    }
    case TimelineTypeStoreModelEnum.PowerLimitChangeRejectedEvent: {
      return translatePowerLimitChangeRejectedEventStoreModel(event, translateService, kwPipe, evseName);
    }
    case TimelineTypeStoreModelEnum.PowerOverrideClearedEvent: {
      return translatePowerOverrideClearedEventStoreModel(event, translateService, evseName);
    }
    case TimelineTypeStoreModelEnum.ManualPrioritySetEvent: {
      return translateManualPrioritySetEventStoreModel(event, translateService, evseName);
    }
    case TimelineTypeStoreModelEnum.ManualPriorityClearedEvent: {
      return translateManualPriorityClearedEventStoreModel(event, translateService, evseName);
    }
    case TimelineTypeStoreModelEnum.SessionClosedEvent: {
      return translateSessionClosedEventStoreModel(event, translateService);
    }
    case TimelineTypeStoreModelEnum.SessionOpenedEvent: {
      return translateSessionOpenedEventStoreModel(event, translateService);
    }
    case TimelineTypeStoreModelEnum.TransactionBlockedEvent: {
      return translateTransactionBlockedEventStoreModel(event, translateService, evseName);
    }
    case TimelineTypeStoreModelEnum.TransactionStartedEvent: {
      return translateTransactionStartedEventStoreModel(event, translateService, evseName);
    }
    case TimelineTypeStoreModelEnum.TransactionRemoteStartEvent: {
      return translateTransactionRemoteStartEventStoreModel(event, translateService, evseName);
    }
    case TimelineTypeStoreModelEnum.TransactionStoppedEvent: {
      return translateTransactionStoppedEventStoreModel(event, translateService, siteId, evseName);
    }
    case TimelineTypeStoreModelEnum.TransactionRemoteStopEvent: {
      return translateTransactionRemoteStopEventStoreModel(event, translateService, evseName);
    }
    case TimelineTypeStoreModelEnum.TriggerMessageEvent: {
      return translateTriggerMessageEventStoreModel(event, translateService, evseName);
    }
    case TimelineTypeStoreModelEnum.UnlockConnectorEvent: {
      return translateUnlockConnectorEventStoreModel(event, translateService, evseName);
    }
    case TimelineTypeStoreModelEnum.UnknownVehiclePluggedInEvent: {
      return translateUnknownVehiclePluggedInEventStoreModel(event, translateService, evseName);
    }
    case TimelineTypeStoreModelEnum.UnrecognizableOcppMessageEvent: {
      return translateUnrecognizableOCPPMessageEventStoreModel(event, translateService, evseName);
    }
    case TimelineTypeStoreModelEnum.VehiclePluggedInEvent: {
      return translateVehiclePluggedInEventStoreModel(event, translateService, siteId, evseName);
    }
    case TimelineTypeStoreModelEnum.VehicleUnpluggedEvent: {
      return translateVehicleUnpluggedEventStoreModel(event, translateService, siteId, evseName);
    }
  }
};

const translateAdaptivePowerCapChangeEventStoreModel = (
  event: AdaptivePowerCapChangeEventStoreModel,
  translateService: TranslateService,
  kwPipe: KwPipe,
  evseName?: string
): EventTranslation => {
  const newAdaptivePowerCap = formatPower(event.newAdaptivePowerCapKw, kwPipe);
  const details: TimelineEventDetail[] = [
    {
      label$: translateService.stream('charging-timeline-events.adaptive-power-cap-change.details.measured-power'),
      value$: of(formatPower(event.measuredPowerKw, kwPipe)),
    },
    {
      label$: translateService.stream('charging-timeline-events.adaptive-power-cap-change.details.applied-power-limit'),
      value$: of(formatPower(event.appliedPowerLimitKw, kwPipe)),
    },
    {
      label$: translateService.stream('charging-timeline-events.adaptive-power-cap-change.details.previous-adaptive-power-cap'),
      value$: of(formatPower(event.previousAdaptivePowerCapKw, kwPipe)),
    },
    {
      label$: translateService.stream('charging-timeline-events.adaptive-power-cap-change.details.new-adaptive-power-cap'),
      value$: of(event.newAdaptivePowerCapKw === undefined ? 'Cleared' : newAdaptivePowerCap),
    },
    {
      label$: translateService.stream('charging-timeline-events.adaptive-power-cap-change.details.cap-delta'),
      value$: of(formatPower(event.capDeltaKw, kwPipe)),
    },
  ];

  return event.newAdaptivePowerCapKw === undefined
    ? {
        title$: translateService.stream('charging-timeline-events.adaptive-power-cap-change.cleared-title'),
        description$: evseName
          ? translateService.stream('charging-timeline-events.adaptive-power-cap-change.cleared-description-with-connector', { evseName })
          : translateService.stream('charging-timeline-events.adaptive-power-cap-change.cleared-description'),
        details,
      }
    : {
        title$: translateService.stream('charging-timeline-events.adaptive-power-cap-change.set-title', {
          newAdaptivePowerCap,
        }),
        description$: evseName
          ? translateService.stream('charging-timeline-events.adaptive-power-cap-change.set-description-with-connector', {
              newAdaptivePowerCap,
              evseName,
            })
          : translateService.stream('charging-timeline-events.adaptive-power-cap-change.set-description', {
              newAdaptivePowerCap,
            }),
        details,
      };
};

const formatPower = (powerKw: number | undefined, kwPipe: KwPipe): string => {
  return powerKw === undefined ? 'None' : kwPipe.transformInstant(powerKw);
};

const translateAuthorizationFailedEventStoreModel = (
  event: AuthorizationFailedEventStoreModel,
  translateService: TranslateService,
  evseName?: string
): EventTranslation => {
  return {
    title$: translateService.stream('charging-timeline-events.authorization-failed.title'),
    description$: evseName
      ? translateService.stream('charging-timeline-events.authorization-failed.description-with-connector', {
          idTag: event.idTag ?? '',
          evseName,
        })
      : translateService.stream('charging-timeline-events.authorization-failed.description', {
          idTag: event.idTag ?? '',
        }),
  };
};

const translateAuthorizationSuccessfulEventStoreModel = (
  event: AuthorizationSuccessfulEventStoreModel,
  translateService: TranslateService,
  evseName?: string
): EventTranslation => {
  return {
    title$: translateService.stream('charging-timeline-events.authorization-successful.title'),
    description$: evseName
      ? translateService.stream('charging-timeline-events.authorization-successful.description-with-connector', {
          evName: event.idTagAssignee ?? '',
          evseName,
        })
      : translateService.stream('charging-timeline-events.authorization-successful.description', {
          evName: event.idTagAssignee ?? '',
        }),
  };
};

const translateBootNotificationEventStoreModel = (
  _event: BootNotificationEventStoreModel,
  translateService: TranslateService
): EventTranslation => {
  return {
    title$: translateService.stream('charging-timeline-events.boot-notification.title'),
    description$: translateService.stream('charging-timeline-events.boot-notification.description'),
  };
};

const translateStatusNotificationEventStoreModel = (
  event: StatusNotificationEventStoreModel,
  translateService: TranslateService,
  evseName?: string
): EventTranslation => {
  const isFaulted = event.status === 'FAULTED';
  const details: TimelineEventDetail[] = [
    { label$: translateService.stream('charging-timeline-events.status-notification.details.error-code'), value$: of(event.errorCode) },
    ...(event.info
      ? [{ label$: translateService.stream('charging-timeline-events.status-notification.details.info'), value$: of(event.info) }]
      : []),
    ...(event.vendorErrorCode
      ? [
          {
            label$: translateService.stream('charging-timeline-events.status-notification.details.vendor-error-code'),
            value$: of(event.vendorErrorCode),
          },
        ]
      : []),
  ];

  return {
    title$: translateService.stream('charging-timeline-events.status-notification.title'),
    description$: evseName
      ? translateService.stream('charging-timeline-events.status-notification.description-with-connector', {
          status: event.status,
          evseName,
        })
      : translateService.stream('charging-timeline-events.status-notification.description', {
          status: event.status,
        }),
    descriptionPartConfigs: [
      {
        type: PartType.Normal,
        class: isFaulted ? 'font-semibold text-chargingstatus-faulted-100' : undefined,
      },
    ],
    details,
  };
};

const translateChargingFaultedEventStoreModel = (
  event: ChargingFaultedEventStoreModel,
  translateService: TranslateService,
  siteId: string,
  evseName?: string
): EventTranslation => {
  const infoFromOcppMessage = (event.ocppMessage ?? '').match(/info=([^),]+)/)?.[1] ?? '';
  const evName = event.evAssetId && event.idTagAssignee ? (event.idTagAssignee ?? '') : '';

  return {
    title$: translateService.stream('charging-timeline-events.charging-faulted.title'),
    description$: evseName
      ? translateService.stream('charging-timeline-events.charging-faulted.description-with-connector', {
          evName,
          evseName,
          info: infoFromOcppMessage,
        })
      : translateService.stream('charging-timeline-events.charging-faulted.description', {
          evName,
          info: infoFromOcppMessage,
        }),
    descriptionPartConfigs: [
      event.idTagAssignee && event.evAssetId
        ? {
            type: PartType.Link,
            link: `/${RouteSegment.Root}/${RouteSegment.Sites}/${siteId}/${RouteSegment.Vehicles}/${event.evAssetId}`,
            class: 'underline',
          }
        : {
            type: PartType.Normal,
          },
    ],
  };
};

const translateCsFaultedEventStoreModel = (_event: CSFaultedEventStoreModel, translateService: TranslateService): EventTranslation => {
  return {
    title$: translateService.stream('charging-timeline-events.cs-faulted.title'),
    description$: translateService.stream('charging-timeline-events.cs-faulted.description'),
  };
};

const translateEvseFaultedEventStoreModel = (
  event: EvseFaultedEventStoreModel,
  translateService: TranslateService,
  evseName?: string
): EventTranslation => {
  const infoFromOcppMessage = (event.ocppMessage ?? '').match(/info=([^),]+)/)?.[1] ?? '';

  return {
    title$: translateService.stream('charging-timeline-events.evse-faulted.title'),
    description$: evseName
      ? translateService.stream('charging-timeline-events.evse-faulted.description-with-connector', {
          evseName,
          info: infoFromOcppMessage,
        })
      : translateService.stream('charging-timeline-events.evse-faulted.description', {
          info: infoFromOcppMessage,
        }),
  };
};

const translateCsStateChangeEventStoreModel = (
  _event: CSStateChangeEventStoreModel,
  translateService: TranslateService
): EventTranslation => {
  return {
    title$: translateService.stream('charging-timeline-events.cs-state-change.title'),
    description$: translateService.stream('charging-timeline-events.cs-state-change.description'),
  };
};

const translateCustomCommandEventStoreModel = (
  event: CustomCommandEventStoreModel,
  translateService: TranslateService
): EventTranslation => {
  return {
    title$: translateService.stream('charging-timeline-events.custom-command.title'),
    description$: translateService.stream('charging-timeline-events.custom-command.description', {
      actor: event.actor,
    }),
  };
};

const translateForceDisconnectEventStoreModel = (
  event: ForceDisconnectEventStoreModel,
  translateService: TranslateService
): EventTranslation => {
  return {
    title$: translateService.stream('charging-timeline-events.force-disconnect.title'),
    description$: translateService.stream('charging-timeline-events.force-disconnect.description', {
      actor: mapActorDisplayName(event.actor),
    }),
  };
};

const translateHardResetEventStoreModel = (event: HardResetEventStoreModel, translateService: TranslateService): EventTranslation => {
  return {
    title$: translateService.stream('charging-timeline-events.hard-reset.title'),
    description$: translateService.stream('charging-timeline-events.hard-reset.description', {
      actor: event.actor,
    }),
  };
};

const translateSoftResetEventStoreModel = (event: SoftResetEventStoreModel, translateService: TranslateService): EventTranslation => {
  return {
    title$: translateService.stream('charging-timeline-events.soft-reset.title'),
    description$: translateService.stream('charging-timeline-events.soft-reset.description', {
      actor: event.actor,
    }),
  };
};

const translatePowerLimitChangeEventStoreModel = (
  event: PowerLimitChangeEventStoreModel,
  translateService: TranslateService,
  kwPipe: KwPipe,
  evseName?: string
): EventTranslation => {
  return event.actor === 'SYSTEM'
    ? {
        title$: translateService.stream('charging-timeline-events.power-limit-change.title'),
        description$: evseName
          ? translateService.stream('charging-timeline-events.power-limit-change.description-with-connector', {
              chargingPowerLimit: kwPipe.transformInstant(event.chargingPowerLimitKw),
              evseName,
            })
          : translateService.stream('charging-timeline-events.power-limit-change.description', {
              chargingPowerLimit: kwPipe.transformInstant(event.chargingPowerLimitKw),
            }),
      }
    : {
        title$: translateService.stream('charging-timeline-events.power-limit-change-manual.title'),
        description$: evseName
          ? translateService.stream('charging-timeline-events.power-limit-change-manual.description-with-connector', {
              actor: event.actor,
              chargingPowerLimit: kwPipe.transformInstant(event.chargingPowerLimitKw),
              evseName,
            })
          : translateService.stream('charging-timeline-events.power-limit-change-manual.description', {
              actor: event.actor,
              chargingPowerLimit: kwPipe.transformInstant(event.chargingPowerLimitKw),
            }),
      };
};

const translatePowerLimitChangeRejectedEventStoreModel = (
  event: PowerLimitChangeRejectedEventStoreModel,
  translateService: TranslateService,
  kwPipe: KwPipe,
  evseName?: string
): EventTranslation => {
  return event.actor === 'SYSTEM'
    ? {
        title$: translateService.stream('charging-timeline-events.power-limit-change-rejected.title'),
        description$: evseName
          ? translateService.stream('charging-timeline-events.power-limit-change-rejected.description-with-connector', {
              chargingPowerLimit: kwPipe.transformInstant(event.chargingPowerLimitKw),
              evseName,
            })
          : translateService.stream('charging-timeline-events.power-limit-change-rejected.description', {
              chargingPowerLimit: kwPipe.transformInstant(event.chargingPowerLimitKw),
            }),
      }
    : {
        title$: translateService.stream('charging-timeline-events.power-limit-change-rejected-manual.title'),
        description$: evseName
          ? translateService.stream('charging-timeline-events.power-limit-change-rejected-manual.description-with-connector', {
              actor: event.actor,
              chargingPowerLimit: kwPipe.transformInstant(event.chargingPowerLimitKw),
              evseName,
            })
          : translateService.stream('charging-timeline-events.power-limit-change-rejected-manual.description', {
              actor: event.actor,
              chargingPowerLimit: kwPipe.transformInstant(event.chargingPowerLimitKw),
            }),
      };
};

const translatePowerOverrideClearedEventStoreModel = (
  event: PowerOverrideClearedEventStoreModel,
  translateService: TranslateService,
  evseName?: string
): EventTranslation => {
  return {
    title$: translateService.stream('charging-timeline-events.power-override-cleared.title'),
    description$: evseName
      ? translateService.stream('charging-timeline-events.power-override-cleared.description-with-connector', {
          actor: event.actor,
          evseName,
        })
      : translateService.stream('charging-timeline-events.power-override-cleared.description', {
          actor: event.actor,
        }),
  };
};

const translateManualPrioritySetEventStoreModel = (
  event: ManualPrioritySetEventStoreModel,
  translateService: TranslateService,
  evseName?: string
): EventTranslation => {
  return {
    title$: translateService.stream('charging-timeline-events.manual-priority-set.title'),
    description$: evseName
      ? translateService.stream('charging-timeline-events.manual-priority-set.description-with-connector', {
          actor: event.actor,
          evseName,
        })
      : translateService.stream('charging-timeline-events.manual-priority-set.description', {
          actor: event.actor,
        }),
  };
};

const translateManualPriorityClearedEventStoreModel = (
  event: ManualPriorityClearedEventStoreModel,
  translateService: TranslateService,
  evseName?: string
): EventTranslation => {
  return {
    title$: translateService.stream('charging-timeline-events.manual-priority-cleared.title'),
    description$: evseName
      ? translateService.stream('charging-timeline-events.manual-priority-cleared.description-with-connector', {
          actor: event.actor,
          evseName,
        })
      : translateService.stream('charging-timeline-events.manual-priority-cleared.description', {
          actor: event.actor,
        }),
  };
};

const translateSessionClosedEventStoreModel = (
  _event: SessionClosedEventStoreModel,
  translateService: TranslateService
): EventTranslation => {
  return {
    title$: translateService.stream('charging-timeline-events.session-closed.title'),
    description$: translateService.stream('charging-timeline-events.session-closed.description'),
  };
};

const translateSessionOpenedEventStoreModel = (
  _event: SessionOpenedEventStoreModel,
  translateService: TranslateService
): EventTranslation => {
  return {
    title$: translateService.stream('charging-timeline-events.session-opened.title'),
    description$: translateService.stream('charging-timeline-events.session-opened.description'),
  };
};

const translateTransactionStartedEventStoreModel = (
  event: TransactionStartedEventStoreModel,
  translateService: TranslateService,
  evseName?: string
): EventTranslation => {
  return {
    title$: translateService.stream('charging-timeline-events.transaction-started.title'),
    description$: evseName
      ? translateService.stream('charging-timeline-events.transaction-started.description-with-connector', {
          idTagAssignee: event.idTagAssignee ?? '',
          evseName,
        })
      : translateService.stream('charging-timeline-events.transaction-started.description', {
          idTagAssignee: event.idTagAssignee ?? '',
        }),
  };
};

const translateTransactionRemoteStartEventStoreModel = (
  event: TransactionRemoteStartEventStoreModel,
  translateService: TranslateService,
  evseName?: string
): EventTranslation => {
  return {
    title$: translateService.stream('charging-timeline-events.transaction-remote-start.title'),
    description$: evseName
      ? translateService.stream('charging-timeline-events.transaction-remote-start.description-with-connector', {
          actor: event.actor,
          idTagAssignee: event.idTagAssignee ?? '',
          evseName,
        })
      : translateService.stream('charging-timeline-events.transaction-remote-start.description', {
          actor: event.actor,
          idTagAssignee: event.idTagAssignee ?? '',
        }),
  };
};

const translateTransactionBlockedEventStoreModel = (
  event: TransactionBlockedEventStoreModel,
  translateService: TranslateService,
  evseName?: string
): EventTranslation => {
  return {
    title$: translateService.stream('charging-timeline-events.transaction-blocked.title'),
    description$: evseName
      ? translateService.stream('charging-timeline-events.transaction-blocked.description-with-connector', {
          idTag: event.idTag ?? '',
          evseName,
        })
      : translateService.stream('charging-timeline-events.transaction-blocked.description', {
          idTag: event.idTag ?? '',
        }),
  };
};

const translateTransactionStoppedEventStoreModel = (
  event: TransactionStoppedEventStoreModel,
  translateService: TranslateService,
  siteId: string,
  evseName?: string
): EventTranslation => {
  const socDisplay = event.soc === undefined ? 'unknown' : `${Math.round(event.soc * 100)}%`;

  return {
    title$: translateService.stream('charging-timeline-events.transaction-stopped.title'),
    description$: evseName
      ? translateService.stream('charging-timeline-events.transaction-stopped.description-with-connector', {
          evName: event.idTagAssignee ?? '',
          evseName,
          socDisplay,
        })
      : translateService.stream('charging-timeline-events.transaction-stopped.description', {
          evName: event.idTagAssignee ?? '',
          socDisplay,
        }),
    descriptionPartConfigs: [
      event.idTagAssignee && event.evAssetId
        ? {
            type: PartType.Link,
            link: `/${RouteSegment.Root}/${RouteSegment.Sites}/${siteId}/${RouteSegment.Vehicles}/${event.evAssetId}`,
            class: 'underline',
          }
        : {
            type: PartType.Normal,
          },
    ],
  };
};

const translateTransactionRemoteStopEventStoreModel = (
  event: TransactionRemoteStopEventStoreModel,
  translateService: TranslateService,
  evseName?: string
): EventTranslation => {
  return {
    title$: translateService.stream('charging-timeline-events.transaction-remote-stop.title'),
    description$: evseName
      ? translateService.stream('charging-timeline-events.transaction-remote-stop.description-with-connector', {
          actor: event.actor,
          evseName,
        })
      : translateService.stream('charging-timeline-events.transaction-remote-stop.description', {
          actor: event.actor,
        }),
  };
};

const translateTriggerMessageEventStoreModel = (
  event: TriggerMessageEventStoreModel,
  translateService: TranslateService,
  evseName?: string
): EventTranslation => {
  return {
    title$: translateService.stream('charging-timeline-events.trigger-message.title'),
    description$: evseName
      ? translateService.stream('charging-timeline-events.trigger-message.description-with-connector', {
          actor: event.actor,
          requestedMessage: event.requestedMessage,
          evseName,
        })
      : translateService.stream('charging-timeline-events.trigger-message.description', {
          actor: event.actor,
          requestedMessage: event.requestedMessage,
        }),
  };
};

const translateUnlockConnectorEventStoreModel = (
  event: UnlockConnectorEventStoreModel,
  translateService: TranslateService,
  evseName?: string
): EventTranslation => {
  return {
    title$: translateService.stream('charging-timeline-events.unlock-connector.title'),
    description$: evseName
      ? translateService.stream('charging-timeline-events.unlock-connector.description-with-connector', {
          actor: event.actor,
          evseName,
        })
      : translateService.stream('charging-timeline-events.unlock-connector.description', {
          actor: event.actor,
        }),
  };
};

const translateUnknownVehiclePluggedInEventStoreModel = (
  _event: UnknownVehiclePluggedInEventStoreModel,
  translateService: TranslateService,
  evseName?: string
): EventTranslation => {
  return {
    title$: translateService.stream('charging-timeline-events.unknown-vehicle-plugged-in.title'),
    description$: evseName
      ? translateService.stream('charging-timeline-events.unknown-vehicle-plugged-in.description-with-connector', {
          evseName,
        })
      : translateService.stream('charging-timeline-events.unknown-vehicle-plugged-in.description'),
  };
};

const translateUnrecognizableOCPPMessageEventStoreModel = (
  _event: UnrecognizableOCPPMessageEventStoreModel,
  translateService: TranslateService,
  evseName?: string
): EventTranslation => {
  return {
    title$: translateService.stream('charging-timeline-events.unrecognizable-ocpp-message.title'),
    description$: evseName
      ? translateService.stream('charging-timeline-events.unrecognizable-ocpp-message.description-with-connector', {
          evseName,
        })
      : translateService.stream('charging-timeline-events.unrecognizable-ocpp-message.description'),
  };
};

const translateVehiclePluggedInEventStoreModel = (
  event: VehiclePluggedInEventStoreModel,
  translateService: TranslateService,
  siteId: string,
  evseName?: string
): EventTranslation => {
  return {
    title$: translateService.stream('charging-timeline-events.vehicle-plugged-in.title', {
      evName: event.idTagAssignee ?? '',
    }),
    description$: evseName
      ? translateService.stream('charging-timeline-events.vehicle-plugged-in.description-with-connector', {
          evName: event.idTagAssignee ?? '',
          evseName,
        })
      : translateService.stream('charging-timeline-events.vehicle-plugged-in.description', {
          evName: event.idTagAssignee ?? '',
        }),
    titlePartConfigs: [
      event.idTagAssignee && event.evAssetId
        ? {
            type: PartType.Link,
            link: `/${RouteSegment.Root}/${RouteSegment.Sites}/${siteId}/${RouteSegment.Vehicles}/${event.evAssetId}`,
            class: 'underline',
          }
        : {
            type: PartType.Normal,
          },
    ],
    descriptionPartConfigs: [
      event.idTagAssignee && event.evAssetId
        ? {
            type: PartType.Link,
            link: `/${RouteSegment.Root}/${RouteSegment.Sites}/${siteId}/${RouteSegment.Vehicles}/${event.evAssetId}`,
            class: 'underline',
          }
        : {
            type: PartType.Normal,
          },
    ],
  };
};

const translateVehicleUnpluggedEventStoreModel = (
  event: VehicleUnpluggedEventStoreModel,
  translateService: TranslateService,
  siteId: string,
  evseName?: string
): EventTranslation => {
  return {
    title$: translateService.stream('charging-timeline-events.vehicle-unplugged.title', {
      evName: event.idTagAssignee ?? '',
    }),
    description$: evseName
      ? translateService.stream('charging-timeline-events.vehicle-unplugged.description-with-connector', {
          evName: event.idTagAssignee ?? '',
          evseName,
        })
      : translateService.stream('charging-timeline-events.vehicle-unplugged.description', {
          evName: event.idTagAssignee ?? '',
        }),
    titlePartConfigs: [
      event.idTagAssignee && event.evAssetId
        ? {
            type: PartType.Link,
            link: `/${RouteSegment.Root}/${RouteSegment.Sites}/${siteId}/${RouteSegment.Vehicles}/${event.evAssetId}`,
            class: 'underline',
          }
        : {
            type: PartType.Normal,
          },
    ],
    descriptionPartConfigs: [
      event.idTagAssignee && event.evAssetId
        ? {
            type: PartType.Link,
            link: `/${RouteSegment.Root}/${RouteSegment.Sites}/${siteId}/${RouteSegment.Vehicles}/${event.evAssetId}`,
            class: 'underline',
          }
        : {
            type: PartType.Normal,
          },
    ],
  };
};

const SYSTEM_ACTOR = 'SYSTEM';

const mapActorDisplayName = (actor: string): string => (actor === SYSTEM_ACTOR ? 'Optimo' : actor);
